import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isAfter, isBefore } from 'date-fns';

import { useLocation } from 'react-router-dom';

import { buildUsabilitySurveyUrl } from 'utils/buildUsabilitySurveyUrl';

import useHomeModals from './hooks/useHomeModals';
import { orderShowModal } from './hooks/orderShowModal';
import { orderShowModalBill } from './hooks/orderShowModalBill';
import { config } from './config';
import { TYPE_PROMOTION } from './enums';
import { getNPSStatus } from './services/npsServices';

import {
  Container,
  Content,
  Button,
  ImgBanner,
  CloseContainer,
  ButtonClose,
  TextWrapper,
  TextTitle,
  TextSubTitle,
  ButtonNPSContainer,
  ButtonNPS,
  OptOutWrapper,
  OptOutCheckbox,
  OptOutLabel,
} from './styles';

const LS_USABILIDADE_DEFINITIVO = 'closeBannerPesquisaUsabilidadeDefinitivo';

const BannerPromotions: React.FC = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [dynamicUrl, setDynamicUrl] = useState<string>('');
  const [optOutUsabilidade, setOptOutUsabilidade] = useState<boolean>(false);
  const { handleNextModal, showActiveModal } = useHomeModals();

  const { pathname } = useLocation();

  const [atualPromotion] = useMemo(() => {
    const path = pathname;
    if (path === '/boletos') {
      const [modalType] = orderShowModalBill.filter(
        (order) => order.id === showActiveModal
      );

      if (!modalType) return [];

      return config.filter((conf) => conf.type === modalType.modal);
    }

    const [modalType] = orderShowModal.filter(
      (order) => order.id === showActiveModal
    );

    if (!modalType) return [];

    return config.filter((conf) => conf.type === modalType.modal);
  }, [showActiveModal, pathname]);

  const getInitialParams = useCallback(async () => {
    if (!atualPromotion) return;
    const initialDate = atualPromotion?.start
      ? isBefore(atualPromotion.start, new Date())
      : true;

    const endDate = atualPromotion?.end
      ? isAfter(atualPromotion.end, new Date())
      : true;

    const isClosedSession =
      localStorage.getItem(
        `@UltragazEmpresarial:${atualPromotion.localStorageKey}`
      ) === 'yes';

    const isClosedUsabilidadeDefinitivo =
      atualPromotion.type === TYPE_PROMOTION.PESQUISA_USABILIDADE &&
      localStorage.getItem(
        `@UltragazEmpresarial:${LS_USABILIDADE_DEFINITIVO}`
      ) === 'yes';

    const isClosed = isClosedSession || isClosedUsabilidadeDefinitivo;

    const hasMFAToken = localStorage.getItem('@UltragazEmpresarial:X-Mfa-Hash');

    const path = window.location.pathname;

    let isNeedShowModal =
      atualPromotion.showPath === path && initialDate && endDate && !isClosed;

    if (atualPromotion.type === TYPE_PROMOTION.NPS && isNeedShowModal) {
      isNeedShowModal = (await getNPSStatus(setDynamicUrl)) && !isClosed;
    }

    if (atualPromotion.type === TYPE_PROMOTION.AVISO_MFA && isNeedShowModal) {
      isNeedShowModal = !hasMFAToken;
    }

    if (
      atualPromotion.type === TYPE_PROMOTION.PESQUISA_USABILIDADE &&
      isNeedShowModal
    ) {
      setDynamicUrl(buildUsabilitySurveyUrl({}));
    }

    setShowModal(isNeedShowModal);
    if (!isNeedShowModal) handleNextModal();
  }, [atualPromotion, handleNextModal]);

  useEffect(() => {
    getInitialParams();
  }, [getInitialParams]);

  const saveClosed = useCallback(() => {
    localStorage.setItem(
      `@UltragazEmpresarial:${atualPromotion.localStorageKey}`,
      'yes'
    );
    handleNextModal();
    setShowModal(false);
  }, [atualPromotion, handleNextModal]);

  const saveClosedPermanentUsabilidade = useCallback(() => {
    localStorage.setItem(
      `@UltragazEmpresarial:${LS_USABILIDADE_DEFINITIVO}`,
      'yes'
    );
    handleNextModal();
    setShowModal(false);
  }, [handleNextModal]);

  const handleClick = useCallback(
    (url: string | undefined) => {
      setTimeout(() => {
        window.open(url, '_blank');
      });
      saveClosed();
    },
    [saveClosed]
  );

  const isUsabilidade =
    atualPromotion?.type === TYPE_PROMOTION.PESQUISA_USABILIDADE;

  const handleClickUsabilidade = useCallback(() => {
    setTimeout(() => {
      window.open(dynamicUrl, '_blank');
    });
    saveClosedPermanentUsabilidade();
  }, [dynamicUrl, saveClosedPermanentUsabilidade]);

  const CustomModal = useMemo(() => {
    if (atualPromotion?.CustomModalComponent) {
      return atualPromotion.CustomModalComponent;
    }
    return null;
  }, [atualPromotion]);

  return (
    <>
      {showModal &&
        (CustomModal ? (
          <CustomModal />
        ) : (
          <Container>
            <Content>
              <CloseContainer>
                <ButtonClose
                  onClick={() => {
                    if (isUsabilidade && optOutUsabilidade) {
                      saveClosedPermanentUsabilidade();
                      return;
                    }
                    saveClosed();
                  }}
                >
                  X
                </ButtonClose>
              </CloseContainer>
              {atualPromotion?.title && (
                <TextWrapper>
                  <TextTitle
                    dangerouslySetInnerHTML={{ __html: atualPromotion.title }}
                  />
                  {atualPromotion?.subTitle && (
                    <TextSubTitle>{atualPromotion.subTitle}</TextSubTitle>
                  )}
                  {atualPromotion?.dinamicLinkPromotion && (
                    <ButtonNPSContainer>
                      <ButtonNPS onClick={() => handleClick(dynamicUrl)}>
                        Quero participar
                      </ButtonNPS>
                    </ButtonNPSContainer>
                  )}
                </TextWrapper>
              )}
              {atualPromotion?.img && (
                <ImgBanner
                  src={atualPromotion.img}
                  customWidth={atualPromotion?.customWidth}
                  customHeight={atualPromotion?.customHeight}
                  customMobileWidth={atualPromotion?.customMobileWidth}
                  customMobileHeight={atualPromotion?.customMobileHeight}
                  alt={
                    isUsabilidade
                      ? 'Pesquisa de usabilidade — clique para participar (abre em nova aba)'
                      : 'Imagem promocional'
                  }
                  isLink={
                    isUsabilidade ||
                    (!!atualPromotion?.linkPromotion &&
                      atualPromotion?.buttonLink === false)
                  }
                  onClick={() => {
                    if (isUsabilidade) {
                      handleClickUsabilidade();
                      return;
                    }
                    if (
                      atualPromotion.linkPromotion &&
                      atualPromotion?.buttonLink === false
                    ) {
                      handleClick(atualPromotion.linkPromotion);
                    }
                  }}
                />
              )}
              {isUsabilidade && (
                <OptOutWrapper>
                  <OptOutCheckbox
                    id="banner-pesquisa-usabilidade-opt-out"
                    type="checkbox"
                    checked={optOutUsabilidade}
                    onChange={(e) => {
                      setOptOutUsabilidade(e.currentTarget.checked);
                    }}
                  />
                  <OptOutLabel htmlFor="banner-pesquisa-usabilidade-opt-out">
                    Não desejo mais visualizar esta mensagem
                  </OptOutLabel>
                </OptOutWrapper>
              )}
              {atualPromotion.linkPromotion && atualPromotion?.buttonLink && (
                <Button
                  onClick={() => handleClick(atualPromotion.linkPromotion)}
                >
                  Votar
                </Button>
              )}
            </Content>
          </Container>
        ))}
    </>
  );
};

export default BannerPromotions;
